import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MailSettings, SwipeAction } from "@websede/contracts";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { Button, Card, ErrorNotice, Field, Select } from "../../components/form.js";

const UNDO_CHOICES = [0, 5, 10, 20, 30] as const;
const SWIPE_CHOICES: SwipeAction[] = ["archive", "delete", "read", "star", "none"];

/** Signature, undo-send delay and the phone list's swipe actions. */
export function MailSettingsPage() {
  const { t } = useTranslation();
  const settings = useQuery({ queryKey: ["mail", "settings"], queryFn: () => api.mail.settings() });
  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4 md:p-8">
      <h1 className="text-[22px] text-ink">{t("nav.mailSettings")}</h1>
      <ErrorNotice error={settings.error} />
      {settings.data ? <MailSettingsForm initial={settings.data} /> : null}
    </section>
  );
}

function MailSettingsForm({ initial }: { initial: MailSettings }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [signature, setSignature] = useState(initial.signature);
  const [undoSeconds, setUndoSeconds] = useState(initial.undoSeconds);
  const [swipeRight, setSwipeRight] = useState(initial.swipeRight);
  const [swipeLeft, setSwipeLeft] = useState(initial.swipeLeft);
  const save = useMutation({
    mutationFn: () => api.mail.updateSettings({ signature, undoSeconds, swipeLeft, swipeRight }),
    onSuccess: (saved) => queryClient.setQueryData(["mail", "settings"], saved),
  });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  return (
    <form onSubmit={onSubmit}>
      <Card title={t("settings.mail.title")}>
        <Field label={t("settings.mail.signature")} hint={t("settings.mail.signatureHint")}>
          <textarea
            className="min-h-28 w-full rounded-md border border-rule bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
            value={signature}
            maxLength={5_000}
            onChange={(e) => setSignature(e.target.value)}
          />
        </Field>
        <Field label={t("settings.mail.undoSend")} hint={t("settings.mail.undoSendHint")}>
          <Select value={undoSeconds} onChange={(e) => setUndoSeconds(Number(e.target.value))}>
            {UNDO_CHOICES.map((seconds) => (
              <option key={seconds} value={seconds}>
                {seconds === 0
                  ? t("settings.mail.undoOff")
                  : t("settings.mail.undoSeconds", { count: seconds })}
              </option>
            ))}
          </Select>
        </Field>
        {(
          [
            [t("settings.mail.swipeRight"), swipeRight, setSwipeRight],
            [t("settings.mail.swipeLeft"), swipeLeft, setSwipeLeft],
          ] as const
        ).map(([label, value, set]) => (
          <Field
            key={label}
            label={label}
            hint={set === setSwipeRight ? t("settings.mail.swipeHint") : undefined}
          >
            <Select value={value} onChange={(e) => set(e.target.value as SwipeAction)}>
              {SWIPE_CHOICES.map((action) => (
                <option key={action} value={action}>
                  {t(`settings.mail.swipeActions.${action}`)}
                </option>
              ))}
            </Select>
          </Field>
        ))}
        <ErrorNotice error={save.error} />
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={save.isPending}>
            {t("common.save")}
          </Button>
          {save.isSuccess ? <span className="text-sm text-ink-3">{t("common.saved")}</span> : null}
        </div>
      </Card>
    </form>
  );
}
